<template>
    <div class="h-100">
        <b-container fluid class="p-0">
            <b-row align-h="center">
                <b-col cols="12" md="10">
                    <b-link :to="{ name: 'settings-ipwhitelisting' }"
                        ><font-awesome-icon icon="arrow-left" />
                        {{ $t("Back") }}
                    </b-link>
                    <div class="d-flex align-items-center mt-1">
                        <h1>
                            {{ $t("mainmenu.settings") }} / {{ $t("settings.ip-whitelisting.IP whitelisting") }} /
                            {{ $t("Add") }}
                        </h1>
                        <b-button id="popover-info" variant="primary" size="sm" class="ml-1 p-0 info-square"
                            >i</b-button
                        >
                    </div>
                    <hr />
                    <b-popover
                        target="popover-info"
                        placement="bottom"
                        triggers="click blur"
                        variant="white"
                        :show.sync="popoverShow"
                    >
                        <template #default>
                            <b-button
                                variant="light"
                                aria-label="secondary"
                                class="px-50 py-25 popover-close"
                                @click="onPopoverClose"
                            >
                                <span aria-hidden="true">&times;</span>
                            </b-button>
                            <h4>{{ $t("settings.ip-whitelisting.IP whitelisting") }}</h4>
                            <p class="text-secondary mb-0">
                                <small>{{ $t("settings.ip-whitelisting.intro1") }}</small>
                            </p>
                        </template>
                    </b-popover>

                    <b-alert variant="warning" show>
                        <div class="alert-body d-flex">
                            <font-awesome-icon icon="exclamation-triangle" size="lg" class="mr-50" />
                            <span class="ml-25">{{ $t("settings.ip-whitelisting.notice") }}</span>
                        </div>
                    </b-alert>

                    <b-alert variant="info" show>
                        <div class="alert-body d-flex">
                            <font-awesome-icon :icon="['fas', 'circle-info']" size="lg" class="mr-50" />
                            <span class="ml-25">{{ $t("settings.ip-whitelisting.current-ip", { ip: ownIp }) }}</span>
                        </div>
                    </b-alert>

                    <validation-observer ref="validationRules" tag="form">
                        <b-form class="mt-1">
                            <validation-provider #default="{ errors }" name="name" rules="required">
                                <b-form-group
                                    id="fieldset-name"
                                    label-cols-sm="4"
                                    label-cols-lg="3"
                                    content-cols-sm
                                    content-cols-lg="7"
                                    label-for="name"
                                    :description="$t('settings.ip-whitelisting.name.description')"
                                    :label="$t('settings.ip-whitelisting.name.label')"
                                    :state="errors.length > 0 ? false : null"
                                >
                                    <b-form-input
                                        id="name"
                                        v-model="ipData.name"
                                        type="text"
                                        :state="errors.length > 0 ? false : null"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
                                #default="{ errors }"
                                name="ipAddress"
                                :rules="{
                                    // Validate that it at least sort of looks like an ip-address
                                    regex: new RegExp(/^\d{1,3}\.\d{1,3}\.(\d{1,3}|\*)\.(\d{1,3}|\*)$/),
                                    required: true,
                                }"
                            >
                                <b-form-group
                                    id="fieldset-ipAddress"
                                    label-cols-sm="4"
                                    label-cols-lg="3"
                                    content-cols-sm
                                    content-cols-lg="7"
                                    label-for="ipAddress"
                                    :description="$t('settings.ip-whitelisting.ipAddress.description')"
                                    :label="$t('settings.ip-whitelisting.ipAddress.label')"
                                    :state="errors.length > 0 ? false : null"
                                >
                                    <b-form-input
                                        id="ipAddress"
                                        v-model="ipData.ipAddress"
                                        type="text"
                                        :state="errors.length > 0 ? false : null"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <hr />

                            <b-form-group
                                label-cols-sm="4"
                                label-cols-lg="3"
                                label-align="right"
                                content-cols-sm
                                content-cols-lg="7"
                            >
                                <b-button
                                    variant="primary"
                                    class="mb-1 mb-sm-0 mr-0 mr-sm-1"
                                    :block="$store.getters['app/currentBreakPoint'] === 'xs'"
                                    @click="submit()"
                                >
                                    {{ $t("settings.ip-whitelisting.Add IP") }}
                                </b-button>
                            </b-form-group>
                        </b-form>
                    </validation-observer>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
import {
    BContainer,
    BButton,
    BCol,
    BRow,
    BForm,
    BFormGroup,
    BFormInput,
    BTable,
    BPagination,
    BCard,
    BAlert,
    BLink,
    BPopover,
} from "bootstrap-vue";
import axios from "@/libs/axios";
import vSelect from "vue-select";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { ref } from "@vue/composition-api";
import { required, regex } from "@validations";
import { useToast } from "vue-toastification/composition";
import i18n from "@/libs/i18n";
import useIpWhitelistingList from "./useIpWhitelistingList";

export default {
    components: {
        BContainer,
        BRow,
        BCol,
        BButton,
        BForm,
        BFormGroup,
        BFormInput,
        BTable,
        BPagination,
        BCard,
        BAlert,
        BLink,
        BPopover,
        vSelect,
        ValidationProvider,
        ValidationObserver,
    },
    data() {
        return {
            required,
            regex,
        };
    },
    setup() {
        const toast = useToast();
        const ipData = ref({
            name: "",
            ipAddress: "",
        });

        const { ownIp, fetchIp } = useIpWhitelistingList();

        // Get the IP of the client.
        fetchIp().catch(() => {});

        /**
         // Popover info
         */
        const popoverShow = ref(false);

        const onPopoverClose = () => {
            popoverShow.value = false;
        };

        return {
            ipData,
            popoverShow,
            toast,
            ownIp,

            onPopoverClose,
        };
    },
    methods: {
        submit() {
            return new Promise((resolve, reject) => {
                this.$refs.validationRules.validate().then((success) => {
                    if (success) {
                        axios
                            .post("/whitelisted-ips", this.ipData)
                            .then(() => {
                                this.$router.push({ name: "settings-ipwhitelisting" }, () => {
                                    this.toast.success(
                                        i18n.t("toast.create.success", {
                                            entity: this.ipData.name,
                                        })
                                    );
                                });
                            })
                            .catch(() => {
                                this.toast.error(
                                    i18n.t("toast.create.error", {
                                        entity: this.ipData.name,
                                    })
                                );
                            });

                        resolve(true);
                    } else {
                        reject();
                    }
                });
            });
        },
    },
};
</script>

<style lang="scss">
@import "~@core/scss/vue/libs/vue-select.scss";
</style>
